/**
 * A Inbox como tela própria (H07).
 *
 * ── O que ela é, se a H04 já tem uma lista ────────────────────────────────
 *
 * A da H04 é a coluna esquerda do atendimento: estreita, para escolher a
 * próxima conversa sem sair de onde se está. Esta é a tela de TRIAGEM — larga,
 * com o contexto comercial inteiro visível, filtros que ficam guardados e
 * rolagem contínua. É onde o gestor olha a fila e onde o vendedor começa o dia.
 *
 * ── O contexto comercial é o motivo dela existir ──────────────────────────
 *
 * A Técnica da task: *"contexto comercial em cada item é o que diferencia da
 * Inbox do Chatwoot. Sem ele, o vendedor precisa abrir cada conversa para saber
 * qual importa."* Etapa, valor e estado do vínculo aparecem na linha.
 *
 * ── E o que ela NÃO inventa ───────────────────────────────────────────────
 *
 * A task pede etiquetas no filtro e indicador de SLA. Nenhum dos dois existe:
 * o cabeçalho do contrato registra que etiquetas não estão em
 * `read.conversations` e que SLA é a D08, *"que ainda não tem regra definida"* —
 * e cita a A07: *"sem regra definida, não inventar indicador"*.
 *
 * O que dá para dizer sem inventar é o FATO: há quanto tempo a conversa espera
 * resposta, quando a última mensagem foi do cliente. Isso não é um veredito de
 * SLA — não há verde nem vermelho, não há prazo. É o dado que já está ali,
 * escrito de um jeito que se lê de relance.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ItemDaInbox } from '@cliqcrm/contracts';
import { useObservarRecursos } from '@/shared/api/assinaturas';
import { Carregando, Defasado, Erro, Vazio } from '@/shared/ui/estados/estados';
import { dinheiro, horaCurta, nomeParaExibir } from '@/shared/lib/formato';
import { nomeDoCanal } from '@/entities/atendimento/canal';
import { NOMES_DE_SITUACAO } from '@/entities/atendimento/conversa';
import { useSessao } from '@/shared/sessao/contexto';
import {
  chaveDeArmazenamento,
  escreverFiltros,
  opcoesComValor,
  seletorVisivel,
  FILTROS_VAZIOS,
  lerFiltros,
  temFiltroAtivo,
  type FiltrosDaInbox,
} from '../lib/filtros';
import { useListaDaInbox } from '../model/usar-lista';
import type { ItemOrdenado } from '../lib/ordem-estavel';
import './inbox.css';
import {
  Avatar,
  Botao,
  CabecalhoDeTela,
  Campo,
  CampoSelecao,
  Chip,
  Lista,
  ListaLinha,
  NaoLidas,
  Tabela,
  type ColunaTabela,
} from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

const SITUACOES = Object.keys(NOMES_DE_SITUACAO);

/*
 * As colunas, no padrão da lista de clientes (28/10): no mouse a tabela
 * compartilhada, com as larguras em proporção; no celular a lista fina.
 */
const COLUNAS: ColunaTabela[] = [
  { chave: 'cliente', rotulo: 'Cliente', classeDaColuna: 'inbox__col-cliente' },
  { chave: 'comercial', rotulo: 'Oportunidade', classeDaColuna: 'inbox__col-comercial' },
  { chave: 'situacao', rotulo: 'Situação', classeDaColuna: 'inbox__col-situacao' },
  { chave: 'canal', rotulo: 'Canal', classeDaColuna: 'inbox__col-canal' },
  { chave: 'ultima', rotulo: 'Última mensagem', classeDaColuna: 'inbox__col-ultima' },
];
const PRIORIDADES = ['urgente', 'alta', 'media', 'baixa'];

export function Inbox({
  aoAbrir,
  canalInicial,
  aoConsumirCanal,
}: {
  aoAbrir: (conversaId: string) => void;
  /**
   * Canal escolhido no detalhamento do painel (H11).
   *
   * Chega como filtro comum, e não como um modo especial: fica visível na
   * barra, conta no indicador de filtro ativo e sai pelo "limpar", como
   * qualquer outro. Um filtro invisível vindo de outra tela produziria a fila
   * curta que ninguém entende — o defeito que o indicador de filtro existe
   * para evitar.
   */
  canalInicial?: string | null;
  /** Avisa que o canal já foi aplicado — ver o comentário na casca. */
  aoConsumirCanal?: () => void;
}): ReactNode {
  const { estado } = useSessao();
  const chave =
    estado.nome === 'autenticado'
      ? chaveDeArmazenamento(estado.me.tenant.slug, estado.me.usuario.id)
      : null;

  const [filtros, definirFiltros] = useState<FiltrosDaInbox>(FILTROS_VAZIOS);

  /**
   * Os filtros guardados entram DEPOIS da primeira renderização.
   *
   * Ler o armazenamento no inicializador do `useState` funcionaria e amarraria
   * o componente ao navegador — o mesmo motivo pelo qual o teste do módulo não
   * precisa de DOM. Aqui o efeito lê uma vez, quando a chave do usuário existe.
   */
  const jaLeu = useRef(false);
  useEffect(() => {
    if (!chave || jaLeu.current) return;
    jaLeu.current = true;
    definirFiltros(
      lerFiltros(localStorage.getItem(chave), {
        situacao: SITUACOES,
        prioridade: PRIORIDADES,
      }),
    );
  }, [chave]);

  /*
   * Aplicado DEPOIS da leitura do armazenamento, de propósito: os dois efeitos
   * correm na mesma montagem, e na ordem inversa o filtro guardado
   * sobrescreveria o canal que a pessoa acabou de escolher no painel.
   */
  useEffect(() => {
    if (!canalInicial) return;
    definirFiltros((atuais) => ({ ...atuais, canal: canalInicial }));
    // Aplicado uma vez. Sem isto ele volta a cada montagem da tela, e a fila
    // aparece filtrada por algo que a pessoa não escolheu.
    aoConsumirCanal?.();
  }, [canalInicial, aoConsumirCanal]);

  useEffect(() => {
    if (!chave || !jaLeu.current) return;
    localStorage.setItem(chave, escreverFiltros(filtros));
  }, [chave, filtros]);

  const lista = useListaDaInbox(filtros);
  const filtrando = temFiltroAtivo(filtros);

  // Tempo real: observa as caixas do que está carregado. É por caixa e não por
  // conversa — aqui interessa a composição da fila, não uma conversa em
  // particular.
  const assinaturas = useMemo(
    () => lista.caixasConhecidas.map((caixa) => `inbox:${caixa.id}`),
    [lista.caixasConhecidas],
  );
  useObservarRecursos(assinaturas);

  return (
    <section className="inbox" aria-label="Fila de conversas">
      <CabecalhoDeTela
        titulo="Fila de conversas"
        apoio="Quem está esperando resposta, e há quanto tempo. A mais antiga manda na ordem."
        {...(lista.totalNoEscopo === null
          ? {}
          : {
              contagem:
                lista.itens.length < lista.totalNoEscopo
                  ? `${lista.itens.length} de ${lista.totalNoEscopo}`
                  : `${lista.totalNoEscopo}`,
              descricaoDaContagem: 'Dentro do seu escopo',
            })}
      />

      {/* A busca e os filtros abaixo do cabeçalho, como na lista de clientes. */}
      <Filtros
        filtros={filtros}
        aoFiltrar={definirFiltros}
        caixas={lista.caixasConhecidas}
        canais={lista.canaisConhecidos}
        filtrando={filtrando}
      />

      {/* O corpo rola; o cabeçalho e a barra ficam. */}
      <div className="inbox__rolagem">
        {lista.frescor?.stale && (
          <Defasado
            projetadoEm={lista.frescor.projetado_em}
            origem="Esta lista"
            aoAtualizar={lista.recarregar}
          />
        )}

        {/* A única coisa que move a lista, e é sempre uma escolha. */}
        {lista.novas > 0 && (
          <Faixa
            tom="defasado"
            acao={{ rotulo: 'Mostrar e reordenar', aoClicar: lista.aceitarOrdem }}
          >
            {lista.novas === 1 ? '1 conversa nova' : `${lista.novas} conversas novas`}
          </Faixa>
        )}

        <Conteudo lista={lista} filtrando={filtrando} aoAbrir={aoAbrir} />
      </div>
    </section>
  );
}

function Conteudo({
  lista,
  filtrando,
  aoAbrir,
}: {
  lista: ReturnType<typeof useListaDaInbox>;
  filtrando: boolean;
  aoAbrir: (conversaId: string) => void;
}): ReactNode {
  if (lista.carregando)
    return <Carregando forma="lista" linhas={8} rotulo="Carregando conversas" />;

  // Só sem nada na tela: com dados carregados, uma atualização que falhou
  // (o limite de requisições, a rede) não apaga o que estava certo.
  if (lista.erro && lista.itens.length === 0) {
    return (
      <div className="inbox__centro">
        <Erro
          erro={lista.erro}
          aoRepetir={lista.recarregar}
          titulo="Não foi possível carregar a fila"
        />
      </div>
    );
  }

  if (lista.itens.length === 0) {
    return (
      <div className="inbox__centro">
        <Vazio
          titulo={filtrando ? 'Nada com esses filtros' : 'Nenhuma conversa esperando'}
          porque={
            filtrando
              ? 'Nenhuma conversa do seu escopo bate com o que você filtrou. Limpe os filtros para ver o resto.'
              : 'Tudo respondido. Conversas novas aparecem aqui sozinhas, sem recarregar.'
          }
        />
      </div>
    );
  }

  const total = lista.totalNoEscopo ?? lista.itens.length;
  return (
    <>
      <Tabela
        classe="inbox__tabela-rolagem"
        classeDaTabela="inbox__tabela"
        rotulo="Fila de conversas"
        densa
        colunas={COLUNAS}
        chaveDe={(linha, indice) => String(linha['id'] ?? indice)}
        linhas={lista.itens.map((ordenado) => celulasDe(ordenado, aoAbrir))}
        // Clicar em qualquer lugar da linha abre a conversa, como no celular.
        aoAbrirLinha={(linha) => aoAbrir(String(linha['id']))}
      />

      {/* No celular, a lista fina: a mesma informação, na ordem em que cabe. */}
      <div className="inbox__lista">
        <Lista fina>
          {lista.itens.map((ordenado) => (
            <LinhaDaConversa key={ordenado.id} ordenado={ordenado} aoAbrir={aoAbrir} />
          ))}
        </Lista>
      </div>

      {lista.temMais && (
        <div className="inbox__mais">
          <Botao
            variante="secundario"
            miudo
            onClick={lista.carregarMais}
            desabilitado={lista.carregandoMais}
            motivo="Carregando…"
          >
            {lista.carregandoMais ? 'Carregando…' : 'Carregar mais'}
          </Botao>
        </div>
      )}

      <p className="inbox__fim">
        {`Mostrando ${lista.itens.length.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')} ${
          total === 1 ? 'conversa' : 'conversas'
        }`}
        {!lista.temMais && total > 0 ? ' · fim da fila' : ''}
      </p>
    </>
  );
}

function Filtros({
  filtros,
  aoFiltrar,
  caixas,
  canais,
  filtrando,
}: {
  filtros: FiltrosDaInbox;
  aoFiltrar: (filtros: FiltrosDaInbox) => void;
  caixas: readonly { id: string; nome: string }[];
  canais: readonly string[];
  filtrando: boolean;
}): ReactNode {
  return (
    <div className="inbox__barra">
      <div className="inbox__busca">
        <Campo
          type="search"
          rotuloOculto="Buscar conversa"
          value={filtros.busca}
          onChange={(e) => aoFiltrar({ ...filtros, busca: e.target.value })}
          placeholder="Buscar por nome ou mensagem"
        />
      </div>
      <div className="inbox__filtros" role="group" aria-label="Filtros da fila">
        <Seletor
          rotulo="Caixa"
          valor={filtros.caixa}
          opcoes={caixas.map((caixa) => [caixa.id, caixa.nome] as const)}
          aoMudar={(v) => aoFiltrar({ ...filtros, caixa: v })}
        />
        <Seletor
          rotulo="Situação"
          valor={filtros.situacao}
          opcoes={Object.entries(NOMES_DE_SITUACAO)}
          aoMudar={(v) => aoFiltrar({ ...filtros, situacao: v })}
        />
        <Seletor
          rotulo="Prioridade"
          valor={filtros.prioridade}
          opcoes={PRIORIDADES.map((p) => [p, p[0]!.toUpperCase() + p.slice(1)] as const)}
          aoMudar={(v) => aoFiltrar({ ...filtros, prioridade: v })}
        />
        <Seletor
          rotulo="Responsável"
          valor={filtros.responsavel}
          opcoes={[['sem_responsavel', 'Sem responsável']]}
          aoMudar={(v) => aoFiltrar({ ...filtros, responsavel: v })}
        />
        {/*
        O CANAL, que existia como filtro e não existia na barra.

        Ele podia chegar do painel e ninguém conseguia vê-lo nem removê-lo — o
        comentário no topo desta tela promete que "fica visível na barra", e não
        ficava. Uma fila filtrada por um controle invisível é uma fila que
        parece quebrada.
      */}
        <Seletor
          rotulo="Canal"
          valor={filtros.canal}
          opcoes={canais.map((canal) => [canal, nomeDoCanal(canal) ?? canal] as const)}
          aoMudar={(v) => aoFiltrar({ ...filtros, canal: v })}
        />

        {filtrando && (
          <Botao variante="fantasma" miudo onClick={() => aoFiltrar(FILTROS_VAZIOS)}>
            Limpar
          </Botao>
        )}
      </div>
    </div>
  );
}

function Seletor({
  rotulo,
  valor,
  opcoes,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  opcoes: readonly (readonly [string, string])[];
  aoMudar: (valor: string) => void;
}): ReactNode {
  /*
   * Some quando não há opção NEM valor escolhido.
   *
   * A condição antiga era só `opcoes.length === 0`, e ela criava a armadilha
   * que esvaziou a fila: as opções de caixa vêm do que a lista carregou, então
   * um filtro que zera a lista zera as opções — e o seletor desaparecia
   * levando junto o único controle capaz de desfazê-lo.
   *
   * Com valor escolhido, ele fica na tela mesmo sem catálogo, mostrando o
   * valor cru. Ver "Caixa: 2" é feio; não ver nada e a fila vazia é pior.
   */
  if (!seletorVisivel(opcoes, valor)) return null;

  // O valor escolhido entra na lista quando não está nela — senão o `<select>`
  // ficaria com a opção vazia selecionada e mentiria sobre o que filtra.
  const comValor = opcoesComValor(opcoes, valor);

  return (
    <CampoSelecao
      rotuloOculto={rotulo}
      ativo={valor !== ''}
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
    >
      <option value="">{rotulo}</option>
      {comValor.map(([chave, nome]) => (
        <option key={chave} value={chave}>
          {nome}
        </option>
      ))}
    </CampoSelecao>
  );
}

/**
 * Há quanto tempo esta conversa espera resposta.
 *
 * NÃO é indicador de SLA. Não há prazo, não há verde nem vermelho, não há
 * veredito — a regra de SLA é a D08 e ainda não existe, e a A07 é explícita:
 * *"sem regra definida, não inventar indicador"*.
 *
 * O que está aqui é o fato que já vem na resposta: a última mensagem foi do
 * cliente, e foi há tanto tempo. Quem decide se isso é ruim é quem lê.
 */
function esperandoDesde(item: ItemDaInbox): string | null {
  const ultima = item.conversa.ultima_mensagem;
  if (!ultima || ultima.direcao !== 'entrada') return null;

  const minutos = Math.floor((Date.now() - Date.parse(ultima.em)) / 60_000);
  if (Number.isNaN(minutos) || minutos < 5) return null;

  if (minutos < 60) return `esperando há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `esperando há ${horas} h`;

  const dias = Math.floor(horas / 24);
  return `esperando há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/** O que vai na coluna da oportunidade: a etapa e o valor, ou o estado do vínculo. */
function Comercial({ item }: { item: ItemDaInbox }): ReactNode {
  const { resumo_comercial: comercial, vinculo } = item;
  if (comercial) {
    return (
      <span className="inbox__comercial">
        {comercial.etapa && <Chip tom="etapa">{comercial.etapa_nome ?? comercial.etapa}</Chip>}
        {dinheiro(comercial.valor) && (
          <span className="inbox__valor">{dinheiro(comercial.valor)}</span>
        )}
        {comercial.stale && (
          <Chip tom="stale" title="Os dados comerciais desta conversa ainda estão atualizando.">
            defasado
          </Chip>
        )}
      </span>
    );
  }
  if (vinculo.estado === 'em_revisao') return <Chip tom="atencao">Identidade em revisão</Chip>;
  if (vinculo.estado === 'sem_vinculo') return <Chip>Sem cadastro</Chip>;
  return <span className="inbox__nada">Sem oportunidade</span>;
}

/** A prévia: a seta quando a última foi nossa, e o trecho. */
function Previa({ item }: { item: ItemDaInbox }): ReactNode {
  const ultima = item.conversa.ultima_mensagem;
  return (
    <span className="inbox__previa">
      {ultima?.direcao === 'saida' && (
        <span className="inbox__seta" aria-label="enviada por nós">
          ↩
        </span>
      )}
      {ultima?.trecho ?? 'Sem mensagens'}
    </span>
  );
}

/** A marca da ordem estável: a conversa mudou, ou saiu da fila, sem pular de lugar. */
function Marca({ marca }: { marca: ItemOrdenado<ItemDaInbox>['marca'] }): ReactNode {
  if (marca === 'nenhuma') return null;
  return (
    <span className={`inbox__marca inbox__marca--${marca}`}>
      {marca === 'atualizado' ? 'atualizada' : 'saiu da fila'}
    </span>
  );
}

function celulasDe(
  ordenado: ItemOrdenado<ItemDaInbox>,
  aoAbrir: (conversaId: string) => void,
): Record<string, ReactNode> {
  const { item, marca } = ordenado;
  const { conversa, cliente, caixa } = item;
  const espera = esperandoDesde(item);
  const naoLida = conversa.nao_lidas > 0;
  return {
    id: conversa.id,
    cliente: (
      <span className="inbox__quem" data-nao-lida={naoLida ? 'sim' : undefined}>
        <Avatar nome={cliente?.nome_exibicao} semente={cliente?.id} tamanho={32} />
        <span className="inbox__duas-linhas">
          {/* O nome é o controle de teclado da linha, com a cara de texto. */}
          <button type="button" className="inbox__abrir" onClick={() => aoAbrir(conversa.id)}>
            <span className="inbox__nome">{nomeParaExibir(cliente)}</span>
          </button>
          <Previa item={item} />
        </span>
        <Marca marca={marca} />
      </span>
    ),
    comercial: <Comercial item={item} />,
    situacao: (
      <span className="inbox__chips">
        <Chip>{NOMES_DE_SITUACAO[conversa.situacao] ?? conversa.situacao}</Chip>
        {conversa.prioridade === 'urgente' && <Chip tom="erro">Urgente</Chip>}
      </span>
    ),
    canal: (
      <span className="inbox__duas-linhas">
        {nomeDoCanal(caixa.canal) ? (
          <Chip tom="canal">{nomeDoCanal(caixa.canal)}</Chip>
        ) : (
          <span className="inbox__nada">—</span>
        )}
        {caixa.nome && <span className="inbox__apoio">{caixa.nome}</span>}
      </span>
    ),
    ultima: (
      <span className="inbox__ultima">
        <span className="inbox__duas-linhas">
          <span className="inbox__hora">{horaCurta(conversa.ultima_mensagem?.em) ?? '—'}</span>
          {espera && <span className="inbox__espera">{espera}</span>}
        </span>
        <NaoLidas quantidade={conversa.nao_lidas} />
      </span>
    ),
  };
}

/** A linha do celular — a mesma informação, na ordem em que ela cabe. */
function LinhaDaConversa({
  ordenado,
  aoAbrir,
}: {
  ordenado: ItemOrdenado<ItemDaInbox>;
  aoAbrir: (conversaId: string) => void;
}): ReactNode {
  const { item, marca } = ordenado;
  const { conversa, cliente } = item;
  const espera = esperandoDesde(item);
  return (
    <ListaLinha
      densa
      naoLida={conversa.nao_lidas > 0}
      rotulo={`Abrir a conversa com ${nomeParaExibir(cliente)}`}
      aoAbrir={() => aoAbrir(conversa.id)}
      inicio={<Avatar nome={cliente?.nome_exibicao} semente={cliente?.id} tamanho={32} />}
      nome={
        <>
          <span className="inbox__nome">{nomeParaExibir(cliente)}</span>
          <Marca marca={marca} />
        </>
      }
      previa={<Previa item={item} />}
      meta={
        <>
          <Comercial item={item} />
          {espera && <span className="inbox__espera">{espera}</span>}
        </>
      }
      direita={
        <>
          <span className="inbox__hora">{horaCurta(conversa.ultima_mensagem?.em) ?? ''}</span>
          <NaoLidas quantidade={conversa.nao_lidas} />
        </>
      }
    />
  );
}
